/**
 * Professional Publishing Elements System
 * Handles industry-standard publishing metadata and legal elements
 */

export interface LibraryOfCongressData {
  catalogingInPublication: string;
  subjectHeadings: string[];
  deweyDecimal: string;
  lccn?: string; // Library of Congress Control Number
  pcn?: string; // Preassigned Control Number
  classificationNumber?: string;
  isbn13?: string;
  isbn10?: string;
}

export interface PublisherInfo {
  publisherName: string;
  imprintName?: string;
  publisherAddress: string;
  website: string;
  email?: string;
  phone?: string;
  logo?: string; // Base64 encoded image
  establishedYear?: number;
  description?: string;
}

export interface LegalNotices {
  copyrightNotice: string;
  allRightsReserved: boolean;
  trademarkNotice?: string;
  disclaimers: string[];
  permissionsAcknowledgments: string[];
  fairUseStatement?: string;
  privacyPolicy?: string;
  warrantyDisclaimer?: string;
}

export interface ProductionCredits {
  editor?: string;
  developmentalEditor?: string;
  copyEditor?: string;
  proofreader?: string;
  coverDesigner?: string;
  interiorDesigner?: string;
  illustrator?: string;
  photographer?: string;
  indexer?: string;
  translator?: string;
  narrator?: string; // For audiobooks
  additionalCredits: Array<{
    role: string;
    name: string;
    website?: string;
  }>;
}

export interface ProfessionalElements {
  libraryOfCongressData: LibraryOfCongressData;
  publisherInfo: PublisherInfo;
  legalNotices: LegalNotices;
  productionCredits: ProductionCredits;
  edition: Edition;
  metadata: PublishingMetadata;
}

export interface Edition {
  editionNumber: number;
  editionName?: string; // "First Edition", "Revised Edition", etc.
  printRun?: number;
  publicationDate: string;
  reprintHistory: Array<{
    date: string;
    changes: string;
    printRun?: number;
  }>;
}

export interface PublishingMetadata {
  genre: string[];
  keywords: string[];
  bisacCodes: string[]; // Book Industry Study Group category codes
  thematicCodes: string[]; // Thema international classification
  targetAudience: string;
  contentWarnings: string[];
  language: string;
  pageCount?: number;
  wordCount?: number;
}

/**
 * Generate standard copyright page content
 */
export function generateCopyrightPage(
  elements: ProfessionalElements,
  bookTitle: string,
  authorName: string,
  publisherName?: string
): string {
  const { legalNotices, publisherInfo, libraryOfCongressData, edition, metadata } = elements;
  
  let copyrightPage = '';

  // Book title and author
  copyrightPage += `**${bookTitle}**\n`;
  copyrightPage += `*by ${authorName}*\n\n`;

  // Publisher information
  if (publisherInfo.publisherName) {
    copyrightPage += `Published by ${publisherInfo.publisherName}\n`;
    if (publisherInfo.imprintName && publisherInfo.imprintName !== publisherInfo.publisherName) {
      copyrightPage += `${publisherInfo.imprintName} imprint\n`;
    }
    copyrightPage += `${publisherInfo.publisherAddress}\n`;
    if (publisherInfo.website) {
      copyrightPage += `${publisherInfo.website}\n`;
    }
    copyrightPage += '\n';
  }

  // Copyright notice
  copyrightPage += `${legalNotices.copyrightNotice}\n`;
  if (legalNotices.allRightsReserved) {
    copyrightPage += 'All rights reserved.\n';
  }
  copyrightPage += '\n';

  // ISBN information
  if (libraryOfCongressData.isbn13) {
    copyrightPage += `ISBN-13: ${libraryOfCongressData.isbn13}\n`;
  }
  if (libraryOfCongressData.isbn10) {
    copyrightPage += `ISBN-10: ${libraryOfCongressData.isbn10}\n`;
  }
  if (libraryOfCongressData.lccn) {
    copyrightPage += `Library of Congress Control Number: ${libraryOfCongressData.lccn}\n`;
  }
  copyrightPage += '\n';

  // Edition information
  if (edition.editionNumber > 1 || edition.editionName) {
    const editionText = edition.editionName || `${getOrdinalNumber(edition.editionNumber)} edition`;
    copyrightPage += `${editionText}\n`;
    copyrightPage += `Published ${edition.publicationDate}\n\n`;
  }

  // Library of Congress data
  if (libraryOfCongressData.catalogingInPublication) {
    copyrightPage += '**Library of Congress Cataloging-in-Publication Data**\n\n';
    copyrightPage += libraryOfCongressData.catalogingInPublication + '\n\n';
  }

  // Legal disclaimers
  if (legalNotices.disclaimers.length > 0) {
    legalNotices.disclaimers.forEach(disclaimer => {
      copyrightPage += disclaimer + '\n\n';
    });
  }

  // Fair use statement
  if (legalNotices.fairUseStatement) {
    copyrightPage += legalNotices.fairUseStatement + '\n\n';
  }

  // Trademark notice
  if (legalNotices.trademarkNotice) {
    copyrightPage += legalNotices.trademarkNotice + '\n\n';
  }

  // Content warnings
  if (metadata.contentWarnings.length > 0) {
    copyrightPage += '**Content Advisory**\n';
    copyrightPage += `This book contains: ${metadata.contentWarnings.join(', ')}\n\n`;
  }

  // Print information
  if (edition.printRun) {
    copyrightPage += `First printing: ${edition.printRun.toLocaleString()} copies\n`;
  }

  // Production location (if applicable)
  copyrightPage += 'Printed in the United States of America\n';

  return copyrightPage.trim();
}

/**
 * Generate production credits page
 */
export function generateProductionCredits(credits: ProductionCredits): string {
  let creditsPage = '**Production Credits**\n\n';

  const standardRoles = [
    { key: 'editor', label: 'Editor' },
    { key: 'developmentalEditor', label: 'Developmental Editor' },
    { key: 'copyEditor', label: 'Copy Editor' },
    { key: 'proofreader', label: 'Proofreader' },
    { key: 'coverDesigner', label: 'Cover Design' },
    { key: 'interiorDesigner', label: 'Interior Design' },
    { key: 'illustrator', label: 'Illustrations' },
    { key: 'photographer', label: 'Photography' },
    { key: 'indexer', label: 'Index' },
    { key: 'translator', label: 'Translation' },
    { key: 'narrator', label: 'Narration' }
  ];

  // Add standard roles
  standardRoles.forEach(role => {
    const person = credits[role.key as keyof ProductionCredits] as string;
    if (person) {
      creditsPage += `**${role.label}:** ${person}\n\n`;
    }
  });

  // Add additional credits
  if (credits.additionalCredits.length > 0) {
    credits.additionalCredits.forEach(credit => {
      creditsPage += `**${credit.role}:** ${credit.name}`;
      if (credit.website) {
        creditsPage += ` (${credit.website})`;
      }
      creditsPage += '\n\n';
    });
  }

  return creditsPage.trim();
}

/**
 * Generate Library of Congress Cataloging-in-Publication data
 */
export function generateCIPData(
  bookTitle: string,
  authorName: string,
  elements: ProfessionalElements
): string {
  const { libraryOfCongressData, metadata } = elements;
  
  let cip = '';
  
  // Author's last name, first name
  const nameParts = authorName.split(' ');
  const lastName = nameParts[nameParts.length - 1];
  const firstName = nameParts.slice(0, -1).join(' ');
  
  cip += `${lastName}, ${firstName}\n`;
  cip += `  ${bookTitle} / ${authorName}\n`;
  cip += `  p. cm.\n`;
  
  if (libraryOfCongressData.isbn13) {
    cip += `  ISBN ${libraryOfCongressData.isbn13}\n`;
  }
  
  // Subject headings
  libraryOfCongressData.subjectHeadings.forEach((heading, index) => {
    cip += `  ${index + 1}. ${heading}`;
    if (index === 0) cip += ` — Fiction.`;
    cip += '\n';
  });
  
  if (libraryOfCongressData.deweyDecimal) {
    cip += `  ${libraryOfCongressData.deweyDecimal}\n`;
  }
  
  if (libraryOfCongressData.lccn) {
    cip += `  ${libraryOfCongressData.lccn}\n`;
  }

  return cip.trim();
}

/**
 * Generate BISAC and Thema category codes
 */
export function generateCategoryCodes(elements: ProfessionalElements): string {
  const { metadata } = elements;
  let codes = '';

  if (metadata.bisacCodes.length > 0) {
    codes += '**BISAC Categories:**\n';
    metadata.bisacCodes.forEach(code => {
      codes += `- ${code}\n`;
    });
    codes += '\n';
  }

  if (metadata.thematicCodes.length > 0) {
    codes += '**Thema Categories:**\n';
    metadata.thematicCodes.forEach(code => {
      codes += `- ${code}\n`;
    });
    codes += '\n';
  }

  return codes.trim();
}

/**
 * Generate permissions and acknowledgments
 */
export function generatePermissionsPage(legalNotices: LegalNotices): string {
  let permissions = '**Permissions and Acknowledgments**\n\n';

  if (legalNotices.permissionsAcknowledgments.length > 0) {
    legalNotices.permissionsAcknowledgments.forEach(ack => {
      permissions += `${ack}\n\n`;
    });
  }

  // Standard permission statement
  permissions += 'The publisher has made every effort to trace copyright holders and to obtain their permission for the use of copyrighted material. The publisher apologizes for any errors or omissions and would be grateful to be notified of any corrections that should be incorporated in future reprints or editions of this book.\n\n';

  return permissions.trim();
}

/**
 * Generate professional metadata for digital distribution
 */
export function generateDigitalMetadata(
  elements: ProfessionalElements,
  bookTitle: string,
  authorName: string
): any {
  const { metadata, libraryOfCongressData, publisherInfo } = elements;

  return {
    title: bookTitle,
    author: authorName,
    publisher: publisherInfo.publisherName,
    isbn: libraryOfCongressData.isbn13,
    language: metadata.language,
    genre: metadata.genre,
    keywords: metadata.keywords,
    description: `A ${metadata.genre.join(', ')} book by ${authorName}`,
    bisacCodes: metadata.bisacCodes,
    thematicCodes: metadata.thematicCodes,
    targetAudience: metadata.targetAudience,
    contentWarnings: metadata.contentWarnings,
    publicationDate: elements.edition.publicationDate,
    pageCount: metadata.pageCount,
    wordCount: metadata.wordCount
  };
}

/**
 * Validate professional elements for completeness
 */
export function validateProfessionalElements(elements: ProfessionalElements): {
  isValid: boolean;
  errors: string[];
  warnings: string[];
} {
  const errors: string[] = [];
  const warnings: string[] = [];

  // Required fields
  if (!elements.legalNotices.copyrightNotice) {
    errors.push('Copyright notice is required');
  }

  if (!elements.publisherInfo.publisherName) {
    errors.push('Publisher name is required');
  }

  if (!elements.edition.publicationDate) {
    errors.push('Publication date is required');
  }

  // Recommended fields
  if (!elements.libraryOfCongressData.isbn13) {
    warnings.push('ISBN-13 is recommended for professional publishing');
  }

  if (elements.libraryOfCongressData.subjectHeadings.length === 0) {
    warnings.push('Subject headings help with discoverability');
  }

  if (elements.metadata.bisacCodes.length === 0) {
    warnings.push('BISAC codes are important for bookstore categorization');
  }

  if (!elements.productionCredits.editor) {
    warnings.push('Editor credit helps establish credibility');
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings
  };
}

/**
 * Helper function to get ordinal numbers
 */
function getOrdinalNumber(num: number): string {
  const suffixes = ['th', 'st', 'nd', 'rd'];
  const value = num % 100;
  
  return num + (suffixes[(value - 20) % 10] || suffixes[value] || suffixes[0]);
}

/**
 * Default professional elements template
 */
export function getDefaultProfessionalElements(): ProfessionalElements {
  return {
    libraryOfCongressData: {
      catalogingInPublication: '',
      subjectHeadings: [],
      deweyDecimal: '',
      lccn: '',
      isbn13: '',
      isbn10: ''
    },
    publisherInfo: {
      publisherName: '',
      publisherAddress: '',
      website: '',
      email: '',
      phone: ''
    },
    legalNotices: {
      copyrightNotice: '',
      allRightsReserved: true,
      disclaimers: [
        'This is a work of fiction. Names, characters, places, and incidents either are the product of the author\'s imagination or are used fictitiously. Any resemblance to actual persons, living or dead, events, or locales is entirely coincidental.'
      ],
      permissionsAcknowledgments: []
    },
    productionCredits: {
      additionalCredits: []
    },
    edition: {
      editionNumber: 1,
      editionName: 'First Edition',
      publicationDate: new Date().getFullYear().toString(),
      reprintHistory: []
    },
    metadata: {
      genre: [],
      keywords: [],
      bisacCodes: [],
      thematicCodes: [],
      targetAudience: 'General Adult',
      contentWarnings: [],
      language: 'English'
    }
  };
}